import api from "@/services/api";
import type { IconName } from "@/services/icons";
import { invalidateRecommendationCaches } from "@/services/recommendations/service";
import type { AISuggestionPersistedStep } from "@/services/recommendations/tasks";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/vue-query";
import { captureException } from "@sentry/vue";
import { reactive, toValue, type MaybeRefOrGetter } from "vue";

export interface AISuggestion {
  id: number;
  title: string;
  description: string;
  category: string;
  priority: "URGENT" | "HIGH" | "MEDIUM" | "LOW";
  tag: string;
  data: Record<string, unknown>;
  batchDate: string;
  createdAt: string;
}

export interface AISuggestionBatchSummary {
  id: number | null;
  batchDate: string | null;
  status: "PENDING" | "RUNNING" | "COMPLETED" | "FAILED" | null;
  tokensUsed: number;
  suggestionCount: number;
  createdAt: string | null;
  completedAt: string | null;
}

export interface AISuggestionBatchStatus extends AISuggestionBatchSummary {
  // The most recent COMPLETED batch, i.e. the one whose suggestions the
  // list endpoint actually serves. Use this for "Generated on X" headers
  // so they don't disagree with the cards being rendered when today's
  // batch is RUNNING or FAILED.
  latestCompleted: AISuggestionBatchSummary | null;
}

export type AISuggestionCategory =
  | "all"
  | "google_ads"
  | "facebook_ads"
  | "seo"
  | "website"
  | "social_media"
  | "ecommerce";

export const AI_SUGGESTION_CATEGORIES: {
  value: AISuggestionCategory;
  label: string;
  icon: IconName;
}[] = [
  { value: "all", label: "All", icon: "list-ul" },
  { value: "google_ads", label: "Google Ads", icon: "google-ads" },
  { value: "facebook_ads", label: "Facebook Ads", icon: "facebook" },
  { value: "seo", label: "Google Search", icon: "google-search-console" },
  { value: "website", label: "Google Analytics 4", icon: "google-analytics" },
  { value: "social_media", label: "Social Media", icon: "comment-dots" },
  { value: "ecommerce", label: "Ecommerce", icon: "shopping-cart" },
];

export interface AICategoryMeta {
  label: string;
  tag: string;
  icon: IconName;
  iconColor: string;
}

export const AI_CATEGORY_META: Record<string, AICategoryMeta> = {
  google_ads: {
    label: "Google Ads",
    tag: "Ads",
    icon: "google-ads",
    iconColor: "",
  },
  microsoft_ads: { label: "Bing Ads", tag: "Ads", icon: "bing", iconColor: "" },
  facebook_ads: {
    label: "Facebook Ads",
    tag: "Ads",
    icon: "facebook",
    iconColor: "text-facebook-blue",
  },
  linkedin_ads: {
    label: "LinkedIn Ads",
    tag: "Ads",
    icon: "linked-in",
    iconColor: "text-linkedin-blue",
  },
  snapchat_ads: {
    label: "Snapchat Ads",
    tag: "Ads",
    icon: "snapchat-ghost",
    iconColor: "text-snapchat-yellow",
  },
  tiktok_ads: {
    label: "TikTok Ads",
    tag: "Ads",
    icon: "tik-tok",
    iconColor: "",
  },
  seo: {
    label: "Google Search",
    tag: "SEO",
    icon: "google-search-console",
    iconColor: "",
  },
  website: {
    label: "Google Analytics 4",
    tag: "Website",
    icon: "google-analytics",
    iconColor: "",
  },
  social_media: {
    label: "Social Media",
    tag: "Social media",
    icon: "comment-dots",
    iconColor: "",
  },
  ecommerce: {
    label: "Ecommerce",
    tag: "Tracking",
    icon: "shopping-cart",
    iconColor: "",
  },
};

export const DEFAULT_CATEGORY_META: AICategoryMeta = {
  label: "General",
  tag: "General",
  icon: "lightbulb-on",
  iconColor: "",
};

export function getCategoryMeta(category: string): AICategoryMeta {
  return AI_CATEGORY_META[category] ?? DEFAULT_CATEGORY_META;
}

function listAISuggestions(): Promise<AISuggestion[]> {
  return api
    .get<AISuggestion[]>("/ai-suggestions/")
    .then((response) => response.data);
}

export function useAISuggestionsQuery(options?: {
  enabled?: MaybeRefOrGetter<boolean>;
}) {
  return reactive(
    useQuery({
      queryKey: ["ai-suggestions"] as const,
      queryFn: listAISuggestions,
      enabled: () => toValue(options?.enabled ?? true),
    }),
  );
}

function getAISuggestionBatchStatus(): Promise<AISuggestionBatchStatus> {
  return api
    .get<AISuggestionBatchStatus>("/ai-suggestions/batch-status/")
    .then((response) => response.data);
}

export function useAISuggestionBatchStatusQuery(options?: {
  enabled?: MaybeRefOrGetter<boolean>;
}) {
  return reactive(
    useQuery({
      queryKey: ["ai-suggestions", "batch-status"] as const,
      queryFn: getAISuggestionBatchStatus,
      enabled: () => toValue(options?.enabled ?? true),
    }),
  );
}

// Fire-and-forget marker for the smart-scheduling "was this viewed?" signal.
// Call from the component that actually renders the list to a real user,
// NOT from TanStack Query's queryFn (otherwise polling re-triggers nightly
// regeneration on every refresh).
function markAISuggestionsViewed(): Promise<void> {
  return api.post("/ai-suggestions/mark-viewed/").then(() => undefined);
}

export function useMarkAISuggestionsViewedMutation() {
  return reactive(
    useMutation({
      mutationFn: markAISuggestionsViewed,
    }),
  );
}

export interface PromoteAISuggestionPayload {
  id: number;
  reviewPeriodDays?: number;
}

function promoteAISuggestion(
  payload: PromoteAISuggestionPayload,
): Promise<{ detail: string; taskId: number; recommendationType: string }> {
  return api
    .post(`/ai-suggestions/${payload.id}/promote/`, {
      reviewPeriodDays: payload.reviewPeriodDays,
    })
    .then((response) => response.data);
}

// Each AI-suggestion mutation (promote / dismiss / restore / step
// updates) ends up touching the underlying RecommendationTask row,
// so it has to refresh both the AI-only cache and every classical
// recommendation cache. Centralized so a future cache addition lands
// in one place — drift here has bitten the PR before.
function invalidateAIRecommendationCaches(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: ["ai-suggestions"] });
  invalidateRecommendationCaches(queryClient);
}

export function usePromoteAISuggestionMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: promoteAISuggestion,
      // Promote creates a new RecommendationTask of type AI_SUGGESTION;
      // task-backed views (My Tasks panel, RecommendationsList AI feed,
      // detail page) and recommendation-level summaries (taskCount,
      // assigneeIds) must refetch or they'll miss the new row until a
      // manual reload.
      onSuccess: () => invalidateAIRecommendationCaches(queryClient),
    }),
  );
}

// Per-step dismissal isn't a supported backend concept — the update_step
// endpoint rejects anything outside TODO/IN_PROGRESS/DONE. Task-level
// dismissal lives on the parent RecommendationTask.
export type StepStatus = "To do" | "In progress" | "Done";

function updateAIStep(payload: {
  taskId: number;
  stepIndex: number;
  status?: StepStatus;
  assigneeId?: number | null;
}): Promise<{
  steps: AISuggestionPersistedStep[];
  status: string;
  taskId: number;
}> {
  return api
    .patch(
      `/recommendations/AI_SUGGESTION/tasks/${payload.taskId}/update-step/`,
      {
        stepIndex: payload.stepIndex,
        ...(payload.status !== undefined && { status: payload.status }),
        // Send `null` for unassign (matching the bulk-claim endpoint).
        // Omit the key entirely when the caller didn't pass an assigneeId
        // so the backend treats the assignee as unchanged.
        ...(payload.assigneeId !== undefined && {
          assigneeId: payload.assigneeId,
        }),
      },
    )
    .then((response) => response.data);
}

export function useUpdateAIStepMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: updateAIStep,
      onSuccess: () => {
        // Step writes roll up into recommendation-level summaries
        // (assigneeIds / done counts), so the recommendation/detail
        // cache has to refresh too — not just the task-backed ones.
        invalidateAIRecommendationCaches(queryClient);
        queryClient.invalidateQueries({
          queryKey: ["ai-recommendation-tracking"],
        });
      },
    }),
  );
}

// Claim every unassigned step for the chosen user (or unassign all when
// assigneeId is null). Symmetric to the classical bulk-assign on a
// recommendation group — the AI equivalent, where "tasks" are the
// ai_steps inside a single AI_SUGGESTION RecommendationTask.
function bulkClaimAISteps(payload: {
  taskId: number;
  assigneeId: number | null;
}): Promise<{
  count: number;
  steps: AISuggestionPersistedStep[];
  taskId: number;
}> {
  return api
    .post(
      `/recommendations/AI_SUGGESTION/tasks/${payload.taskId}/bulk-claim-steps/`,
      { assigneeId: payload.assigneeId },
    )
    .then((response) => response.data);
}

export function useBulkClaimAIStepsMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: bulkClaimAISteps,
      // Reconcile on both outcomes via onSettled so success and failure can't
      // drift on which caches they invalidate. On error also capture to Sentry:
      // the AI endpoints are in api.ts `noLogoutOn403Urls`, so a 403/500 is
      // otherwise swallowed and the UI would read as a successful claim — the
      // refetch reconciles displayed ownership/progress to true server state.
      onError: (err) => captureException(err),
      onSettled: () => {
        // Bulk claim mutates step ownership/progress that's also
        // displayed on the AI tracking page — invalidate the tracking
        // cache too so an open tracking view doesn't stay stale.
        invalidateAIRecommendationCaches(queryClient);
        queryClient.invalidateQueries({
          queryKey: ["ai-recommendation-tracking"],
        });
      },
    }),
  );
}

function dismissAISuggestion(id: number): Promise<{ detail: string }> {
  return api
    .post(`/ai-suggestions/${id}/dismiss/`)
    .then((response) => response.data);
}

export function useDismissAISuggestionMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: dismissAISuggestion,
      onMutate: async (dismissedId) => {
        await queryClient.cancelQueries({ queryKey: ["ai-suggestions"] });
        const previous = queryClient.getQueryData<AISuggestion[]>([
          "ai-suggestions",
        ]);
        // Only seed the optimistic snapshot when there's already a
        // cache entry to filter from. Writing `[]` on a cold cache
        // would freeze that empty list in TanStack's 10s stale window
        // — the next mount would render "no recommendations" until
        // the dismiss-onError rollback ran (which can't happen on
        // success), even though real suggestions exist.
        if (previous !== undefined) {
          queryClient.setQueryData<AISuggestion[]>(
            ["ai-suggestions"],
            previous.filter((s) => s.id !== dismissedId),
          );
        }
        return { previous };
      },
      onError: (_err, _id, context) => {
        if (context?.previous === undefined) {
          // No prior snapshot to restore. If we wrote one optimistically
          // (we didn't, see onMutate), or if anyone else is reading
          // this key, force a refetch on next access so the cache
          // doesn't stick at a stale value.
          queryClient.removeQueries({
            queryKey: ["ai-suggestions"],
            exact: true,
          });
          return;
        }
        // Don't replay the entire pre-mutation snapshot — that can
        // resurrect items dismissed by other concurrent mutations that
        // already succeeded. Invalidate instead so the next read pulls
        // the canonical list from the server, which already reflects
        // every dismiss that did land.
        queryClient.invalidateQueries({ queryKey: ["ai-suggestions"] });
      },
      onSuccess: () => {
        invalidateAIRecommendationCaches(queryClient);
        // Dismissing a promoted AI suggestion mutates the same parent
        // RecommendationTask the tracking detail page reads. Without
        // this invalidation an open detail page keeps the old status
        // badge / progress visible until manual refresh.
        queryClient.invalidateQueries({
          queryKey: ["ai-recommendation-tracking"],
        });
      },
    }),
  );
}
